<template>
  <div>
    <div v-if="loading" class="text-center py-3"><i class="fas fa-spinner fa-spin"></i></div>

    <div v-else>
      <div v-if="assignment">
        <b-card class="mb-3">
          <b-row class="align-items-center">
            <b-col cols="12" md="8">
              <h5 class="mb-1">{{ assignment.name || $t('Active Assignment') }}</h5>
              <div class="text-muted small">
                <span v-if="assignment.vehicle_type_id">{{ getVehicleTypeLabel(assignment.vehicle_type_id) }}</span>
                <span v-if="assignment.center_name"> — {{ assignment.center_name }}</span>
                <span v-if="assignment.unit_name"> / {{ assignment.unit_name }}</span>
              </div>
              <div class="mt-2">
                <div>
                  <strong>{{ $t('Start') }}:</strong> {{ formatDate(assignment.start_date) }}
                </div>
                <div v-if="assignment.end_date">
                  <strong>{{ $t('End') }}:</strong> {{ formatDate(assignment.end_date) }}
                </div>
                <div v-else class="text-success">
                  <strong>{{ $t('Active') }}</strong>
                </div>
              </div>
              <div class="mt-2" v-if="assignment.notes"><strong>{{ $t('Notes') }}:</strong> {{ assignment.notes }}</div>
            </b-col>
            <b-col cols="12" md="4" class="text-right">
              <div class="mb-2"><strong>{{ $t('Devices') }}:</strong>
                <span v-if="(assignment.devices || assignment.device_vehicle_assignment || []).length">
                  <span v-for="device in (assignment.devices || assignment.device_vehicle_assignment)" :key="device.id || device.device_id" class="badge badge-secondary mr-1">
                    {{ formatDeviceLabel(device) }}
                  </span>
                </span>
                <span v-else class="text-muted">{{ $t('N/A') }}</span>
              </div>
              <div class="d-flex justify-content-end">
                <b-button variant="danger" size="sm" class="mr-2" @click="finish" v-if="assignment.status === 'active'">{{ $t('Finish Assignment') }}</b-button>
              </div>
            </b-col>
          </b-row>

              <b-row class="mt-3" v-if="(assignment.devices || assignment.device_vehicle_assignment || []).length">
            <b-col cols="12">
              <b-row>
                <b-col cols="12" md="6" lg="4" v-for="device in assignment.devices || assignment.device_vehicle_assignment" :key="device.id || device.device_id" class="mb-2">
                  <div class="border rounded p-2 h-100">
                    <div class="font-weight-bold">{{ formatDeviceLabel(device) }}</div>
                    <div class="text-muted small">{{ (device.device && device.device.description) || device.description || device.name || '-' }}</div>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <div v-else>
        <p class="text-muted">{{ $t('No active assignment for this vehicle') }}</p>
      </div>

      <div v-if="assignments && assignments.length">
        <h6 class="mt-2">{{ $t('Assignment History') }}</h6>
        <b-row>
          <b-col cols="12" md="6" lg="4" v-for="assignmentItem in assignments" :key="assignmentItem.id" class="mb-3" v-if="!isActive(assignmentItem)">
            <b-card>
              <h6 class="mb-1">{{ assignmentItem.name || $t('Assignment') }}</h6>
              <div class="text-muted small">{{ assignmentItem.center_name || '' }} {{ assignmentItem.unit_name ? ' / ' + assignmentItem.unit_name : '' }}</div>
              <div class="mt-2 small"><strong>{{ $t('Period') }}:</strong>
                <div>
                  <strong>{{ $t('Start') }}:</strong> {{ formatDate(assignmentItem.start_date) }}
                </div>
                <div v-if="assignmentItem.end_date">
                  <strong>{{ $t('End') }}:</strong> {{ formatDate(assignmentItem.end_date) }}
                </div>
                <div v-else class="text-success">
                  <strong>{{ $t('Active') }}</strong>
                </div>
              </div>
              <div class="mt-1 small"><strong>{{ $t('Devices') }}:</strong>
                <div>
                  <span v-if="assignmentItem.devices && assignmentItem.devices.length">
                    <span v-for="device in assignmentItem.devices" :key="device.id" class="badge badge-secondary mr-1">{{ formatDeviceLabel(device) }}</span>
                  </span>
                  <span v-else class="text-muted">{{ $t('N/A') }}</span>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-modal v-model="showTransferModal" :title="$t('Assign Vehicle')" size="lg" centered backdrop @hide="resetTransferForm">
      <div v-if="transferLoading" class="text-center py-3"><i class="fas fa-spinner fa-spin"></i></div>
      <div v-else>
        <ValidationObserver ref="transferFormObserver" v-slot="{ validate }">
          <b-form @submit.prevent="handleTransfer">
            <b-row>
              <b-col cols="12" md="6">
                <ValidationProvider name="Assignment Name" rules="required" v-slot="{ errors }">
                  <label><span class="required">*</span>{{ $t('Assignment Name') }} <span class="required">*</span></label>
                  <b-form-input v-model="transferForm.name" :state="errors.length ? false : null" />
                                  <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" md="6">
                <ValidationProvider name="Vehicle Type" rules="required" v-slot="{ errors }">
                  <label>{{ $t('Vehicle Type') }} <span class="required">*</span></label>
                  <v-select :options="vehicleTypes" :reduce="vt => vt.id" label="sigla" v-model="transferForm.vehicle_type_id" :class="{ 'is-invalid': errors.length }" />
                  <span class="invalid-feedback" v-if="errors.length">{{ $t(errors[0]) }}</span>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" md="6">
                <ValidationProvider name="Center" rules="required" v-slot="{ errors }">
                  <label>{{ $t('Center') }} <span class="required">*</span></label>
                  <v-select :options="centers" :reduce="center => center.id" label="name" v-model="transferForm.center_id" @input="loadUnitsForTransfer" :class="{ 'is-invalid': errors.length }" />
                  <span class="invalid-feedback" v-if="errors.length">{{ $t(errors[0]) }}</span>
                </ValidationProvider>
              </b-col>

              <b-col cols="12" md="6">
                <ValidationProvider name="Unit" rules="required" v-slot="{ errors }">
                  <label>{{ $t('Unit') }} <span class="required">*</span></label>
                  <v-select :options="transferUnits" :reduce="unit => unit.id" label="name" v-model="transferForm.unit_id" :class="{ 'is-invalid': errors.length }" @input="onUnitChange" />
                  <span class="invalid-feedback" v-if="errors.length">{{ $t(errors[0]) }}</span>
                </ValidationProvider>
              </b-col>

              <b-col cols="12">
                <label>{{ $t('Devices') }}</label>
                <v-select :options="devices" :multiple="true" :reduce="device => device.id" label="display" v-model="transferForm.device_ids" :disabled="devicesLoading" :placeholder="devicesLoading ? $t('Loading devices...') : $t('Select devices')" />
              </b-col>

              <b-col cols="12">
                <ValidationProvider name="Start Date" rules="required" v-slot="{ errors }">
                  <label>{{ $t('Start Date') }} <span class="required">*</span></label>
                  <b-form-input type="date" v-model="transferForm.start_date" :state="errors.length ? false : null" />
                  <b-form-invalid-feedback v-if="errors.length">{{ $t(errors[0]) }}</b-form-invalid-feedback>
                </ValidationProvider>
              </b-col>

              <b-col cols="12">
                <ValidationProvider name="Notes" rules="required" v-slot="{ errors }">
                  <label>{{ $t('Notes') }} <span class="required">*</span></label>
                  <b-form-textarea v-model="transferForm.notes" rows="3" :state="errors.length ? false : null" />
                  <b-form-invalid-feedback v-if="errors.length">{{ $t(errors[0]) }}</b-form-invalid-feedback>
                </ValidationProvider>
              </b-col>
            </b-row>
          </b-form>
        </ValidationObserver>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="showTransferModal = false">{{ $t('Cancel') }}</b-button>
        <b-button
          variant="primary"
          class="button-primary ml-2"
          @click="handleTransfer"
          :disabled="transferLoading"
        >
          <template v-if="transferLoading">
            <i class="fas fa-spinner fa-spin"></i> {{ $t('Assigning') }}...
          </template>
          <template v-else>
            <i class="fas fa-exchange-alt"></i> {{ $t('Assign') }}
          </template>
        </b-button>
      </template>
    </b-modal>
    <b-modal
      v-model="showConfirmFinishModal"
      :title="$t('Active Assignment Found')"
      size="md"
      centered
      backdrop
      hide-footer
    >
      <div class="py-3">
        <p>
          {{ $t('There is an active assignment for this vehicle. Do you want to finish it before creating a new assignment?') }}
        </p>
        <div class="text-right">
          <b-button variant="secondary" class="mr-2" @click="showConfirmFinishModal = false">
            {{ $t('Cancel') }}
          </b-button>
          <b-button variant="danger" @click="finishActiveAssignment">
            {{ $t('Finish Assignment') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import vSelect from 'vue-select';

export default {
  components: { 'v-select': vSelect },
  props: { vehicleId: { type: [String, Number], required: true } },
  data() {
    return {
      loading: false,
      assignment: null,
      assignments: [],
      form: {
        name: '', vehicle_type_id: '', center_id: '', unit_id: '', start_date: '', notes: '', status: '', device_ids: []
      },
      transferForm: {
        name: '', vehicle_type_id: '', center_id: '', unit_id: '', start_date: '', notes: '', device_ids: []
      },
      transferUnits: [],
      vehicleTypes: [],
      centers: [],
      units: [],
      devices: [],
      devicesLoading: false,
      statusOptions: [{ value: 'active', text: 'Active' }, { value: 'finished', text: 'Finished' }],
      showTransferModal: false,
      showConfirmFinishModal: false,
      transferLoading: false
    };
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      try { return new Date(date).toLocaleDateString('pt-BR'); } catch (e) { return date; }
    },

    formatDateTime(datetime) {
      if (!datetime) return '-';
      try { return new Date(datetime).toLocaleString('pt-BR'); } catch (e) { return datetime; }
    },

    formatDeviceLabel(device) {
      if (!device) return '-';
      const node = device.device || device;
      const imei = node.imei || node.serial_number || node.identifier || node.id || node.device_id;
      const desc = node.description || node.name || node.model || node.type;
      if (imei && desc) return `${imei} - ${desc}`;
      if (imei) return String(imei);
      if (desc) return desc;
      return '-';
    },
    async load() {
      if (!this.vehicleId) return;
      this.loading = true;
      try {
        const [typesRes, centersRes, assignmentsRes] = await Promise.all([
          axios.get(`${this.$store.state.api}vehicle_types`).catch(() => ({ data: [] })),
          axios.get(`${this.$store.state.api}centers`).catch(() => ({ data: [] })),
          axios.get(`${this.$store.state.api}vehicles/${this.vehicleId}/assignments`).catch(() => ({ data: {} }))
        ]);

        this.vehicleTypes = typesRes.data.data || typesRes.data || [];
        this.centers = centersRes.data.data || centersRes.data || [];

  const assignmentsRaw = assignmentsRes.data && assignmentsRes.data.data ? assignmentsRes.data.data : (assignmentsRes.data || []);
  this.assignments = Array.isArray(assignmentsRaw) ? assignmentsRaw : (assignmentsRaw.assignments || []);

  let active = null;
  if (assignmentsRes.data && assignmentsRes.data.active_assignment) active = assignmentsRes.data.active_assignment;
  if (!active && Array.isArray(this.assignments)) active = this.assignments.find(assignmentItem => assignmentItem && (assignmentItem.status === 'active' || !assignmentItem.end_date));
  this.assignment = active || null;
        if (this.assignment) {
          this.form = {
            name: this.assignment.name || '',
            vehicle_type_id: this.assignment.vehicle_type_id || '',
            center_id: this.assignment.center_id || '',
            unit_id: this.assignment.unit_id || '',
            start_date: this.assignment.start_date ? this.assignment.start_date.split(' ')[0] : '',
            notes: this.assignment.notes || '',
            status: this.assignment.status || '',
            device_ids: (this.assignment.devices || []).map(device => device.id || device.device_id)
          };
          this.loadUnitsForCenter(this.form.center_id, false);
        }
        this.assignments = (this.assignments || []).map(assignmentItem => ({
          ...assignmentItem,
          center_name: (this.centers.find(center => center.id === assignmentItem.center_id) || {}).name,
          unit_name: (this.units.find(unit => unit.id === assignmentItem.unit_id) || {}).name
        }));
              this.assignments = (this.assignments || []).slice().sort((left, right) => {
                const leftDate = left && left.start_date ? new Date(left.start_date) : new Date(0);
                const rightDate = right && right.start_date ? new Date(right.start_date) : new Date(0);
                return rightDate - leftDate;
              });
      } catch (e) {
        this.$error(e);
      } finally {
        this.loading = false;
      }
    },

    isActive(assignmentItem) {
      if (!assignmentItem) return false;
      if (this.assignment && assignmentItem.id && this.assignment.id && Number(assignmentItem.id) === Number(this.assignment.id)) return true;
      return assignmentItem.status === 'active' || !assignmentItem.end_date;
    },

    getVehicleTypeLabel(id) {
      const found = (this.vehicleTypes || []).find(vt => String(vt.id) === String(id));
      return found ? (found.sigla || found.name || found.id) : id;
    },
    async loadUnitsForCenter(centerId, clearUnit = true) {
      if (!centerId) { this.units = []; if (clearUnit) this.form.unit_id = ''; return; }
      try {
        const res = await axios.get(`${this.$store.state.api}centers/unit/${centerId}`);
        this.units = res.data || [];
        if (clearUnit) this.form.unit_id = '';
      } catch (e) { this.$error(e); }
    },
    async loadUnitsForTransfer(centerId) {
      if (!centerId) { this.transferUnits = []; this.transferForm.unit_id = ''; return; }
      try {
        const res = await axios.get(`${this.$store.state.api}centers/unit/${centerId}`);
        this.transferUnits = res.data || [];
      } catch (e) { this.$error(e); }
    },
    async loadDevicesForTransfer(unitId = null) {
      try {
        this.devicesLoading = true;
        let devicesRes;
        if (unitId) {
          devicesRes = await axios.get(`${this.$store.state.api}units/devices/${unitId}`);
        } else {
          devicesRes = await axios.get(`${this.$store.state.api}devices`);
        }

        const activeAssignmentsRes = await axios.get(`${this.$store.state.api}vehicle_assignments?status=active`).catch(() => ({ data: [] }));

        const rawDevices = devicesRes.data.data || devicesRes.data || [];
        const rawActiveAssignments = activeAssignmentsRes.data.data || activeAssignmentsRes.data || [];

        const activeDeviceIds = new Set();
        (rawActiveAssignments || []).forEach(assignment => {
          if (!assignment) return;
          if (assignment.vehicle_id && String(assignment.vehicle_id) === String(this.vehicleId)) return;
          if (Array.isArray(assignment.devices)) assignment.devices.forEach(device => { if (device && (device.id || device.device_id)) activeDeviceIds.add(Number(device.id || device.device_id)); });
          if (Array.isArray(assignment.device_vehicle_assignment)) assignment.device_vehicle_assignment.forEach(dva => { if (dva && dva.device_id) activeDeviceIds.add(Number(dva.device_id)); });
        });

        const available = (rawDevices || []).filter(device => device && !activeDeviceIds.has(Number(device.id)));
        this.devices = (available || []).map(device => ({ id: device.id, display: (device.imei || device.serial_number || device.id) + (device.description ? ' - ' + device.description : '') }));
      } catch (e) { this.$error(e); } finally { this.devicesLoading = false; }
    },
    onUnitChange(unitId) {
      this.transferForm.device_ids = [];
      if (unitId) {
        this.loadDevicesForTransfer(unitId);
      } else {
        this.devices = [];
      }
    },
    onCenterChange(val) { this.loadUnitsForCenter(val); },
    async openTransfer() {
      if (this.assignment && this.assignment.status === 'active') {
        this.showConfirmFinishModal = true;
        return;
      }
      try {
        await this.load();
        this.resetTransferForm();
        this.showTransferModal = true;
      } catch (e) {
        this.$error(e);
      }
    },
    async finishActiveAssignment() {
      this.showConfirmFinishModal = false;
      if (!this.assignment) return;
      try {
        await this.finish();
        this.showTransferModal = true;
      } catch (e) {
      }
    },
  onTransferCreated() { this.showTransferModal = false; this.load(); this.$emit('assignment-changed', { closeParent: false }); },
    async handleTransfer() {
      if (this.$refs.transferFormObserver) {
        const ok = await this.$refs.transferFormObserver.validate().catch(() => false);
        if (!ok) {
          this.$message(this.$t('Please fill all required fields'), '', 'error');
          return;
        }
      }

      try {
        this.transferLoading = true;
        const response = await axios.post(`${this.$store.state.api}vehicles/${this.vehicleId}/assignments/transfer`, this.transferForm);
        if (response.data && response.data.success) {
          this.$message(this.$t('Success'), this.$t('Vehicle Assignment successfully'), 'success');
          this.showTransferModal = false;
          this.load();
          this.$emit('assignment-changed', { closeParent: false, updatedVehicle: { level: 1 } });
        }
      } catch (e) { this.$error(e); } finally { this.transferLoading = false; }
    },
    resetTransferForm() {
      this.transferForm = { name: '', vehicle_type_id: '', center_id: '', unit_id: '', start_date: '', notes: '', device_ids: [] };
      this.transferUnits = [];
      this.transferLoading = false;
    },
    async save(validate) {
      if (validate && this.$refs.formObserver) {
        const ok = await this.$refs.formObserver.validate();
        if (!ok) return;
      }
      if (!this.assignment) return;
      try {
        this.loading = true;
        const payload = { ...this.form };
        const res = await axios.put(`${this.$store.state.api}vehicle_assignments/${this.assignment.id}`, payload);
        if (res.data && res.data.success) {
          this.$message(this.$t('Success'), this.$t('Assignment updated'), 'success');
          this.load();
          const newLevel = this.form && this.form.status ? (this.form.status === 'active' ? 1 : (this.form.status === 'finished' ? 2 : this.data.level)) : this.data.level;
          this.$emit('assignment-changed', { closeParent: false, updatedVehicle: { level: newLevel } });
        }
      } catch (e) { this.$error(e); } finally { this.loading = false; }
    },
    async finish() {
      if (!this.assignment) return;
      try {
        this.loading = true;
        const res = await axios.post(`${this.$store.state.api}vehicle_assignments/${this.assignment.id}/finish`, { finish_reason: this.$t('Finished from vehicle view'), end_date: new Date().toISOString().split('T')[0] });
        if (res.data && res.data.success) {
          this.$message(this.$t('Success'), this.$t('Assignment  finished successfully'), 'success');
          if (this.assignment) {
            this.assignment.status = 'finished';
            this.assignment.end_date = new Date().toISOString().split('T')[0];
          }
          this.load();
          this.$emit('assignment-changed', { closeParent: false, setVehicleInactive: true, lastAssignment: this.assignment });
        }
      } catch (e) { this.$error(e); } finally { this.loading = false; }
    }
  },
  mounted() { this.load(); }
};
</script>

<style scoped>
.v-select { width: 100%; }
.assignment-card {
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
  padding: 12px;
}
</style>
